import React, { useEffect, useMemo } from 'react';
import type { GetStationaryPrintOrderCardDetailsByOrderNumberQuery } from '../../Card.graphql.generated';
import {
  useGetStationaryPrintOrderCardDetailsByOrderNumberQuery,
  useSetRecipientSessionIDLazyQuery,
  useStationeryTrackDigitalCardViewMutation,
} from '../../Card.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { Box, Flex, GlobalStyle, IconButton, NoSsr } from '@withjoy/joykit';
import type { DraftJson } from '../CardCustomizer';
import { IRevealType, Reveal } from '@shared/components/GiftWrap/Reveal/Reveal';
import { useWindowSize } from '@shared/components/GiftWrap/Reveal/hooks';
import { InteractiveCard } from '../Dashboard/components/GalleryTile/InteractiveCard';
import { ReactComponent as JoyLogo } from '@assets/joy-logo.svg';
import { IsWithinRecipientViewProvider } from '../CardCustomizer/components/IsWithinRecipientViewContext';
import { Link, useTranslation } from '@shared/core';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { hexToRgb, isHexColor, toRGBObject } from '@shared/components/GiftWrap/Reveal/utils';
import { Error } from './Error';
import { useQueryParamHelper } from '@shared/core/queryString';
import { DEFAULT_ANIMATION, DEFAULT_BACKGROUND_COLOR, DEFAULT_ENVELOPE_COLOR } from '../DigitalCardCustomizer/constants';
import { isLightColor } from '@shared/utils/color';
import { getDimensions } from '@apps/card/utils';
import { Seo } from '@shared/components/Seo';
import { useDigitalCardMusicPlayback } from '@apps/card/hooks/useDigitalCardMusicPlayback';
import { getDigitalCardMusicUrl } from '@apps/card/hooks/useDigitalCardMusic';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useColorways } from '@apps/card/hooks/useColorways';
import '@assets/charm.css';

const DEFAULT_OPEN_DELAY = 4000;
const PADDING = 40;
// const INNER_PADDING = 10;
// Larger number will make card larger in display.
// Most displays have much higher pixel density than 72.
// if we want to be closer to real size onscreen we need to use a higher number.
const DISPLAY_PPI = 120;

export const Footer = ({ backgroundColor }: { backgroundColor: string }) => {
  const { getPublicRootPath } = useCardsRouterContext();
  const { recipientPremiumEcardClick } = useCardTelemetry();
  const { tq } = useTranslation('cardGuest');

  const isBackgroundLight = useMemo(() => {
    if (!backgroundColor) {
      return true;
    }

    let r: number, g: number, b: number;

    const rgbValues = toRGBObject(backgroundColor);
    if (rgbValues) {
      r = rgbValues.r;
      g = rgbValues.g;
      b = rgbValues.b;
    } else if (isHexColor(backgroundColor)) {
      const rgb = hexToRgb(backgroundColor);
      r = rgb.r;
      g = rgb.g;
      b = rgb.b;
    } else {
      return true;
    }

    return isLightColor(r, g, b);
  }, [backgroundColor]);

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={6}
      margin={0}
      bottom={0}
      left={0}
      right={0}
      padding={6}
      color={isBackgroundLight ? 'black' : 'white'}
    >
      <IconButton
        size="sm"
        aria-label={tq`Back`}
        variant="ghost"
        as={'a'}
        href="https://withjoy.com"
        target="_blank"
        height={{ _: 6, sm2: 7 }}
        color="inherit"
        onClick={() => {
          recipientPremiumEcardClick('footer_logo');
        }}
      >
        <JoyLogo height="100%" />
      </IconButton>
      <Flex
        flexDirection="row"
        alignItems="center"
        gap={6}
      >
        <Box
          as="a"
          href="https://withjoy.com/help/en/?utm_source=e-card-invite&utm_medium=viral&utm_campaign=guest-help"
          target="_blank"
          typographyVariant="label2"
          color="inherit"
          onClick={() => {
            recipientPremiumEcardClick('footer_help');
          }}
        >
          {tq`Guest Help`}
        </Box>
        <Box
          as="a"
          href="https://withjoy.com/"
          target="_blank"
          typographyVariant="label2"
          color="inherit"
          onClick={() => {
            recipientPremiumEcardClick('footer_about');
          }}
        >
          {tq`About`}
        </Box>
        <Box
          as={Link}
          to={getPublicRootPath()}
          target="_blank"
          typographyVariant="label2"
          color="inherit"
          onClick={() => {
            recipientPremiumEcardClick('footer_browseDesigns');
          }}
        >
          {tq`Browse Joy Cards`}
        </Box>
      </Flex>
    </Flex>
  );
};

/*
const urlRegex =/(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;

const linkify = (text) => {
  
  return text.replace(urlRegex, function(url) {
      return '<a href="' + url + '">' + url + '</a>';
  });
}

const getClickableAreas = (cardJson: DraftJson) => {
  const clickableAreas = cardJson?.card?.back?.layers?.reduce((results, layer ) => {
    if (layer.type === 'text') {
      const match = layer.textData.content.match(
    results.push({x: layer.layout.x, y: layer.layout.y, width: layer.layout.width, height: layer.layout.height});
    }
  }, []);
  return clickableAreas;
}
*/
/**
 * Base component for displaying a recipient preview of a premium ecard, excluding the footer.
 */
export const RecipientPremiumEcardViewContent = (props: { cardJson: DraftJson; footer?: () => React.ReactNode }) => {
  const { width: winWidth, height: winHeight } = useWindowSize();
  const { cardJson, footer } = props;
  const [isMusicEnabled, isMusicFlagLoading] = useIsFeatureEnabled('printDigitalCardMusic');
  const musicUrl = isMusicEnabled ? getDigitalCardMusicUrl(cardJson.digitalCustomizations?.musicId) : undefined;
  const { playMusic, fadeOutMusic } = useDigitalCardMusicPlayback(isMusicEnabled);

  const cardFront = cardJson?.card?.front;
  const cardBack = cardJson?.card?.back;

  // Resolve the baked-in colorway selection into `--N` CSS vars so the front's
  // `var(--N)` layer tokens render (digital tokens aren't collapsed
  // server-side).
  const { cssVarStyle: frontCssVarStyle } = useColorways(cardFront);

  const shouldHideCardBackPreview = cardBack?.layout === 'blank' || cardBack?.layout === 'placeholder';
  const shape = cardJson?.customizations?.shape;

  const dimensions = getDimensions(cardJson.card);
  const width = dimensions?.width ?? 0;
  const height = dimensions?.height ?? 0;
  const MAX_HEIGHT = height * DISPLAY_PPI;
  const MAX_WIDTH = width * DISPLAY_PPI;

  // TODO: get background color from theme and remove fallback
  const availableHeight = winHeight - 80;
  const backgroundColor = cardJson.digitalCustomizations?.backgroundColor ?? DEFAULT_BACKGROUND_COLOR;
  const themeColor = cardJson.digitalCustomizations?.envelopeColor ?? DEFAULT_ENVELOPE_COLOR;
  const animation = cardJson.digitalCustomizations?.envelopeAnimation ?? DEFAULT_ANIMATION;
  const divisor = availableHeight / MAX_HEIGHT;
  const WIDTH = availableHeight > MAX_HEIGHT ? MAX_WIDTH : MAX_WIDTH * divisor;
  let revealWidth = WIDTH;

  if (winWidth <= WIDTH + PADDING) {
    revealWidth = winWidth - PADDING;
  }

  const contentHeight = Math.floor(revealWidth * (height / width));
  const contentWidth = Math.floor(revealWidth);

  return (
    <Box backgroundColor={backgroundColor}>
      <Box
        margin={0}
        padding={0}
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor={backgroundColor}
      >
        <Reveal
          animation={animation}
          preventToggleOnContentClick
          width={revealWidth}
          themeColor={themeColor}
          type={IRevealType.PremiumEnvelope}
          backgroundColor={backgroundColor}
          // Hold the envelope shut until the flag settles, so an early open neither skips the music nor auto-opens a music card.
          autoPlayDelay={isMusicFlagLoading || musicUrl ? undefined : DEFAULT_OPEN_DELAY}
          onOpenByUser={() => playMusic(musicUrl)}
          onCloseByUser={fadeOutMusic}
          loading={isMusicFlagLoading}
          bottomOffset={{ _: 160, sm2: 200 }}
          envelopeSiblingElement={footer}
        >
          <Flex
            flexDirection="column"
            marginX="auto"
            width={contentWidth}
            gap={6}
          >
            <InteractiveCard
              width={contentWidth}
              height={contentHeight}
              format={StationeryDraftFormat.digital}
              page={cardFront}
              shape={shape}
              interactive={cardJson.digitalCustomizations?.interactive}
              cssVarStyle={frontCssVarStyle}
            />
            {cardBack && !shouldHideCardBackPreview && (
              <InteractiveCard
                width={contentWidth}
                height={contentHeight}
                format={StationeryDraftFormat.digital}
                page={cardBack}
                shape={shape}
                interactive={cardJson.digitalCustomizations?.interactive}
              />
            )}
          </Flex>
        </Reveal>
      </Box>
    </Box>
  );
};

interface RecipientPremiumEcardViewProps {
  orderNumber: string;
  data: GetStationaryPrintOrderCardDetailsByOrderNumberQuery | undefined;
}

export const RecipientPremiumEcardViewWrapper = ({ orderNumber, data }: RecipientPremiumEcardViewProps) => {
  const paramHelper = useQueryParamHelper();
  const [trackDigitalCardView] = useStationeryTrackDigitalCardViewMutation();
  const [getInviteeSession] = useSetRecipientSessionIDLazyQuery();
  const { recipientPremiumEcardClick } = useCardTelemetry({ page: 'premiumEcardRecipientView' });
  const handleLinkClick = useEventCallback(() => recipientPremiumEcardClick('interactiveCard'));

  useEffect(() => {
    const utmSource = paramHelper.getValueString('utm_source')?.replace('print-coupleToGuests-', ''); // triming so we can accomodate some sms links with incorrect utm_source
    const personId = paramHelper.getValueString('pid');
    const isValidPersonId = personId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(personId);

    if (data && isValidPersonId) {
      const eventId = data.stationeryPrintOrderCardDetailsByOrderNumber.eventId;
      // Don't block on invalid utmSource, but ensure it is a valid value or 'none'
      // Valid sources:
      // - 'link': User copied link to clipboard (resend menu action, current)
      // - 'copy': Legacy value for copied links, kept for backward compatibility with existing URLs
      // - 'whatsapp', 'sms', 'email': Shared via respective channels
      const validUtms = !!utmSource && ['email', 'sms', 'link', 'copy', 'whatsapp'].includes(utmSource);

      // Map legacy 'copy' to 'link' before sending to backend
      const normalizedSource = validUtms ? (utmSource === 'copy' ? 'link' : utmSource) : 'none';

      trackDigitalCardView({
        variables: {
          data: {
            cardOrderId: data.stationeryPrintOrderCardDetailsByOrderNumber.orderId,
            personId,
            src: normalizedSource,
          },
        },
      });

      if (eventId) {
        getInviteeSession({
          variables: {
            eventID: eventId,
            inviteeID: personId,
          },
        });
      }
    }
  }, [data, getInviteeSession, paramHelper, trackDigitalCardView]);

  const cardJson = data?.stationeryPrintOrderCardDetailsByOrderNumber?.detailsJSONBlob as DraftJson;
  if (!cardJson) {
    return <Error orderNumber={orderNumber} />;
  }

  const backgroundColor = cardJson.digitalCustomizations?.backgroundColor ?? DEFAULT_BACKGROUND_COLOR;

  return (
    <>
      <IsWithinRecipientViewProvider onLinkClick={handleLinkClick}>
        <RecipientPremiumEcardViewContent
          cardJson={cardJson}
          footer={() => <Footer backgroundColor={backgroundColor} />}
        />
      </IsWithinRecipientViewProvider>
    </>
  );
};

export const RecipientPremiumEcardView = (props: { orderNumber: string }) => {
  const { tq } = useTranslation('cardGuest');
  const { data } = useGetStationaryPrintOrderCardDetailsByOrderNumberQuery({
    variables: {
      orderNumber: props.orderNumber,
    },
    batchMode: 'fast',
  });

  return (
    <>
      <Seo
        title={tq`You've Got a Card`}
        description={tq`Check out what's inside. Hope you enjoy it!`}
        ogImage={data?.stationeryPrintOrderCardDetailsByOrderNumber?.digitalEnvelopeImageUrl || ''}
      />
      <GlobalStyle />
      <NoSsr>
        <RecipientPremiumEcardViewWrapper
          data={data}
          orderNumber={props.orderNumber}
        />
      </NoSsr>
    </>
  );
};
